import { when } from 'mobx';
import React from 'react';
import { useHistory } from 'react-router-dom';

import useErnie from 'shared/hooks/use-ernie';
import { useStores } from 'src/hooks/use-stores';
import { isAdminAccessible } from 'shared/helpers/users';
import PublicEnv from 'shared/utils/public-env';

export default function TransferRoute(): null {
  const history = useHistory();
  const { User } = useStores();
  const showErnie = useErnie();

  // Read the token BEFORE stripping it from the URL below.
  const urlParams = new URLSearchParams(window.location.search);
  const transferToken = urlParams.get('transferToken');

  React.useEffect(() => {
    // The transfer token is single-use and short-lived. Strip it from the URL immediately so
    // /transfer?transferToken=... can't be bookmarked, shared, or replayed from browser history
    // (a stale/expired token replayed later would only ever produce a genuine, but misleading,
    // rejection long after the real sign-in attempt happened).
    if (transferToken) {
      const url = new URL(window.location.href);
      url.searchParams.delete('transferToken');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    async function handleLogin(token: string): Promise<void> {
      const response = await User.loginViaToken(token);

      if (response.success) {
        when(
          // Preserve the exact legacy readiness boundary rather than accepting merely truthy values.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
          () => User.exists === true,
          () => {
            const adminUser = isAdminAccessible(User.user);

            if (adminUser) {
              history.push('/redirect');
            } else {
              window.location.href = `${PublicEnv.consumerUrl}/login`;
            }
          }
        );
      } else if (response.isNetworkError) {
        // The request never reached the server -- this is NOT a token problem. Don't tell the
        // user their token is invalid (that sends support down the wrong path, e.g. telling them
        // to clear cookies). The already-consumed token can't be resubmitted here anyway, so send
        // them back to /login to obtain a fresh one rather than retrying with the spent token.
        showErnie('We had trouble connecting. Please try logging in again.', 'danger');
        history.push('/login');
      } else {
        showErnie('That transfer token is no longer valid. Please log in again.', 'danger');
        history.push('/login');
      }
    }

    if (!transferToken) {
      history.push('/login');
    } else {
      void handleLogin(transferToken);
    }
    // Intentionally run once on mount: this route consumes a single-use transfer token from the
    // URL and must strip it from the URL immediately, regardless of later re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
